import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
}

export function useNotifications() {
  useLanguage();
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check if notifications are supported
    if ('Notification' in window && 'serviceWorker' in navigator) {
      setIsSupported(true);
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = async () => {
    if (!isSupported) {
      toast({
        title: "Não suportado",
        description: "Notificações não são suportadas neste navegador.",
        variant: "destructive",
      });
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast({
          title: "Notificações ativadas",
          description: "Você receberá alertas sobre suas métricas e conversões.",
        });
        return true;
      } else {
        toast({
          title: "Permissão negada",
          description: "Você não receberá notificações push.",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const sendNotification = async (options: NotificationOptions) => {
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      // Try to use service worker for better reliability
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(options.title, {
          body: options.body,
          icon: options.icon || '/pwa-192x192.png',
          badge: options.badge || '/pwa-192x192.png',
          tag: options.tag,
          data: options.data,
        });
      } else {
        // Fallback to regular notification
        new Notification(options.title, {
          body: options.body,
          icon: options.icon || '/pwa-192x192.png',
        });
      }
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  // Example notification functions for specific events
  const notifyConversion = (amount: number, videoName: string) => {
    sendNotification({
      title: translateInterfaceText("🎉 Nova conversão!"),
      body: `Você recebeu uma conversão de R$ ${amount.toFixed(2)} no vídeo "${videoName}"`,
      tag: 'conversion',
    });
  };

  const notifyMetricMilestone = (metric: string, value: number) => {
    sendNotification({
      title: translateInterfaceText("📊 Meta alcançada!"),
      body: `Seu vídeo atingiu ${value} ${metric}!`,
      tag: 'milestone',
    });
  };

  const notifyHighEngagement = (videoName: string, engagementRate: number) => {
    sendNotification({
      title: translateInterfaceText("🔥 Alto engajamento!"),
      body: `O vídeo "${videoName}" está com ${engagementRate}% de engajamento`,
      tag: 'engagement',
    });
  };

  const notifyPeakTime = (time: string) => {
    sendNotification({
      title: translateInterfaceText("⏰ Horário de pico"),
      body: `Seu vídeo está recebendo mais visualizações agora (${time})`,
      tag: 'peak-time',
    });
  };

  return {
    permission,
    isSupported,
    requestPermission,
    sendNotification,
    notifyConversion,
    notifyMetricMilestone,
    notifyHighEngagement,
    notifyPeakTime,
  };
}
